/**
 * mdast utilities for [[page link]] syntax.
 *
 * This module provides:
 * - `pageLinkFromMarkdown`: Converts pageLink tokens to mdast HTML nodes
 * - `escapePageTitleForMarkdown`: Escapes titles for [[...]] serialization
 * - `unescapePageLinkTitle`: Unescapes titles from [[...]] format
 *
 * ## How it works
 *
 * When micromark tokenizes [[Page Title]], it creates tokens:
 * - pageLinkMarker: [[
 * - pageLinkTitle: Page Title
 * - pageLinkMarker: ]]
 *
 * The fromMarkdown handler accumulates the title content (handling multiple
 * pageLinkTitle tokens in case of false close attempts with single ]) and
 * creates an HTML node with data attributes that can be processed later.
 *
 * ## Escaping
 *
 * For PARSING (markdown -> nodes):
 * - \] becomes ]
 * - \\ becomes \
 * - \| becomes |
 * - Any \X becomes X (generic escape)
 *
 * For SERIALIZING (nodes -> markdown):
 * - ] becomes \] (to prevent premature closing)
 * - \ becomes \\ (to preserve literal backslashes)
 * - | becomes \| (to preserve literal pipes)
 *
 * @module
 */
import { findPageLinkEnd } from "./pageLinkScan";

/**
 * Escape a string for safe inclusion in an HTML attribute value.
 * - & must be escaped first (before other escapes)
 * - " must be escaped to &quot;
 * - < and > must be escaped to prevent breaking out of attribute context
 * - Other characters like / don't need escaping in attributes
 *
 * Note: fromMarkdown parses HTML nodes and re-serializes them, so we need
 * proper HTML entity encoding here.
 */
function escapeHtmlAttribute(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

/**
 * Unescape page title from [[link]] format.
 *
 * Reverses ALL backslash escapes: \X becomes X for any character X.
 * This is used when parsing markdown to get the display title.
 *
 * @param linkText - The escaped link text from markdown
 * @returns The unescaped display title
 *
 * @example
 * ```ts
 * unescapePageLinkTitle("Page with \\] bracket") // "Page with ] bracket"
 * unescapePageLinkTitle("Page with \\\\ backslash") // "Page with \ backslash"
 * unescapePageLinkTitle("Normal title") // "Normal title"
 * ```
 */
export function unescapePageLinkTitle(linkText: string): string {
	return linkText.replace(/\\(.)/g, "$1");
}

function findUnescapedSeparator(value: string, separator: string): number {
	let escaped = false;

	for (let index = 0; index < value.length; index += 1) {
		const current = value[index];
		if (escaped) {
			escaped = false;
			continue;
		}

		if (current === "\\") {
			escaped = true;
			continue;
		}

		if (current === separator) {
			return index;
		}
	}

	return -1;
}

export function parseObsidianPageLinkTitle(linkText: string): {
	pageTitle: string;
	displayText?: string;
} {
	const aliasSeparatorIndex = findUnescapedSeparator(linkText, "|");
	const rawTarget =
		aliasSeparatorIndex === -1 ? linkText : linkText.slice(0, aliasSeparatorIndex);
	const rawDisplayText =
		aliasSeparatorIndex === -1 ? undefined : linkText.slice(aliasSeparatorIndex + 1);

	const pageTitle = unescapePageLinkTitle(rawTarget);
	const displayText =
		rawDisplayText === undefined || rawDisplayText === ""
			? undefined
			: unescapePageLinkTitle(rawDisplayText);

	return { pageTitle, displayText };
}

export function buildPageLinkHtmlFromTitle(linkText: string): string {
	const { pageTitle, displayText } = parseObsidianPageLinkTitle(linkText);
	const attributes = [
		`data-page-link="true"`,
		`data-page-title="${escapeHtmlAttribute(pageTitle)}"`,
	];

	if (displayText) {
		attributes.push(`data-page-display="${escapeHtmlAttribute(displayText)}"`);
	}

	return `<span ${attributes.join(" ")}></span>`;
}

function buildRawToDisplayedIndexMap(rawValue: string, displayedValue: string): number[] {
	const rawToDisplayedIndex: number[] = [];
	let rawIndex = 0;
	let displayedIndex = 0;

	while (rawIndex < rawValue.length && displayedIndex < displayedValue.length) {
		const rawCharacter = rawValue[rawIndex];
		const displayedCharacter = displayedValue[displayedIndex];

		if (
			rawCharacter === "\\" &&
			rawIndex + 1 < rawValue.length &&
			rawValue[rawIndex + 1] === displayedCharacter
		) {
			rawToDisplayedIndex[rawIndex + 1] = displayedIndex;
			rawIndex += 2;
			displayedIndex += 1;
			continue;
		}

		if (rawCharacter === displayedCharacter) {
			rawToDisplayedIndex[rawIndex] = displayedIndex;
			rawIndex += 1;
			displayedIndex += 1;
			continue;
		}

		rawIndex += 1;
	}

	return rawToDisplayedIndex;
}

function buildDisplayedToRawIndexMap(rawValue: string, displayedValue: string): number[] {
	const displayedToRawIndex: number[] = [];
	let rawIndex = 0;
	let displayedIndex = 0;

	while (rawIndex < rawValue.length && displayedIndex < displayedValue.length) {
		const rawCharacter = rawValue[rawIndex];
		const displayedCharacter = displayedValue[displayedIndex];

		if (
			rawCharacter === "\\" &&
			rawIndex + 1 < rawValue.length &&
			rawValue[rawIndex + 1] === displayedCharacter
		) {
			displayedToRawIndex.push(rawIndex);
			rawIndex += 2;
			displayedIndex += 1;
			continue;
		}

		if (rawCharacter === displayedCharacter) {
			displayedToRawIndex.push(rawIndex);
			rawIndex += 1;
			displayedIndex += 1;
			continue;
		}

		rawIndex += 1;
	}

	return displayedToRawIndex;
}

function replaceEmbeddedPageLinksInText(
	value: string,
	rawValue = value
): Array<{ type: "text" | "html"; value: string }> {
	const nodes: Array<{ type: "text" | "html"; value: string }> = [];
	const displayedToRawIndex = buildDisplayedToRawIndexMap(rawValue, value);
	const rawToDisplayedIndex = buildRawToDisplayedIndexMap(rawValue, value);
	let cursor = 0;

	while (cursor < value.length) {
		const embedStart = value.indexOf("![[", cursor);
		if (embedStart === -1) {
			nodes.push({ type: "text", value: value.slice(cursor) });
			break;
		}

		const rawEmbedStart = displayedToRawIndex[embedStart];
		let displayedBackslashCount = 0;
		for (let index = embedStart - 1; index >= 0 && value[index] === "\\"; index -= 1) {
			displayedBackslashCount += 1;
		}
		if (displayedBackslashCount % 2 === 1) {
			cursor = embedStart + 1;
			continue;
		}

		if (rawEmbedStart !== undefined) {
			let backslashCount = 0;
			for (
				let index = rawEmbedStart - 1;
				index >= 0 && rawValue[index] === "\\";
				index -= 1
			) {
				backslashCount += 1;
			}

			if (backslashCount % 2 === 1) {
				cursor = embedStart + 1;
				continue;
			}
		}

		if (
			rawEmbedStart !== undefined &&
			rawValue[rawEmbedStart] === "\\" &&
			rawValue[rawEmbedStart + 1] === "!"
		) {
			cursor = embedStart + 1;
			continue;
		}

		if (embedStart > cursor) {
			nodes.push({ type: "text", value: value.slice(cursor, embedStart) });
		}

		const rawLinkEnd = findPageLinkEnd(rawValue, rawEmbedStart + 3);
		if (rawLinkEnd === -1) {
			nodes.push({ type: "text", value: value.slice(embedStart) });
			break;
		}

		const displayedLinkEnd = rawToDisplayedIndex[rawLinkEnd];
		if (displayedLinkEnd === undefined) {
			nodes.push({ type: "text", value: value.slice(embedStart) });
			break;
		}

		const rawTitle = rawValue.slice(rawEmbedStart + 3, rawLinkEnd);
		nodes.push({
			type: "html",
			value: buildPageLinkHtmlFromTitle(rawTitle),
		});
		cursor = displayedLinkEnd + 2;
	}

	return nodes.filter((node) => node.value.length > 0);
}

/**
 * Escape page title for markdown serialization.
 *
 * The pageTitle attribute contains the UNESCAPED display title.
 * This function adds escapes needed for the [[...]] markdown syntax:
 * - \ becomes \\ (to preserve literal backslashes)
 * - ] becomes \] (to prevent premature closing with ]])
 * - | becomes \| (to prevent alias parsing)
 *
 * Does NOT escape [ or / since they don't break the syntax:
 * - [ alone doesn't close the link (we need ]])
 * - / is preserved for parent/child format (e.g., [[Parent/Child]])
 *
 * @param title - The unescaped display title
 * @returns The escaped title safe for [[...]] markdown
 *
 * @example
 * ```ts
 * escapePageTitleForMarkdown("Page with ] bracket") // "Page with \\] bracket"
 * escapePageTitleForMarkdown("Page with \\ backslash") // "Page with \\\\ backslash"
 * escapePageTitleForMarkdown("Parent/Child") // "Parent/Child" (/ not escaped)
 * ```
 */
export function escapePageTitleForMarkdown(title: string): string {
	return title.replace(/\\/g, "\\\\").replace(/\|/g, "\\|").replace(/\]/g, "\\]");
}

/**
 * Creates an mdast-util-from-markdown extension for [[page links]].
 *
 * This handler converts pageLink tokens from micromark into HTML nodes
 * that can be further processed by remarkProseMirror or other tools.
 *
 * The output HTML node looks like:
 * ```html
 * <span data-page-link="true" data-page-title="Page Title"></span>
 * ```
 *
 * @returns An mdast fromMarkdown extension
 *
 * @example
 * ```ts
 * import { fromMarkdown } from 'mdast-util-from-markdown';
 * import { pageLinkSyntax } from './micromark-page-link';
 * import { pageLinkFromMarkdown } from './mdast-page-link';
 *
 * const tree = fromMarkdown('[[My Page]]', {
 *   extensions: [pageLinkSyntax()],
 *   mdastExtensions: [pageLinkFromMarkdown()],
 * });
 * ```
 */
export function pageLinkFromMarkdown() {
	let titleBuffer = "";
	let pendingSingleBracket = false;

	return {
		enter: {
			/**
			 * On entering a pageLink, reset the title buffer
			 */
			pageLink() {
				titleBuffer = "";
				pendingSingleBracket = false;
			},
		},
		exit: {
			/**
			 * On exiting a pageLinkTitle token, accumulate the content.
			 * Multiple pageLinkTitle tokens can occur when there's a single ]
			 * that doesn't close the link.
			 */

			pageLinkTitle(this: any, token: any) {
				const raw = this.sliceSerialize(token);
				// If we had a pending single bracket from a false close attempt,
				// include it in the title
				if (pendingSingleBracket) {
					titleBuffer += "]";
					pendingSingleBracket = false;
				}
				titleBuffer += raw;
			},

			/**
			 * Track when we exit a pageLinkMarker that wasn't a real close.
			 * A single ] (not followed by another ]) should be part of the title.
			 */

			pageLinkMarker(this: any, token: any) {
				const raw = this.sliceSerialize(token);
				// If marker is a single ], it was a false close attempt
				if (raw === "]") {
					pendingSingleBracket = true;
				}
			},

			/**
			 * On exiting the pageLink, create an HTML node with the title.
			 * This HTML node will be processed by htmlHandlers in the
			 * remark-prosemirror pipeline.
			 *
			 * We bypass toHtml() to avoid HTML entity encoding of the title
			 * (which would corrupt titles containing /, &, etc.).
			 */
			pageLink(this: any) {
				const htmlValue = buildPageLinkHtmlFromTitle(titleBuffer);

				// Add the HTML node to the parent
				const parent = this.stack[this.stack.length - 1];
				if (parent && "children" in parent) {
					parent.children.push({
						type: "html",
						value: htmlValue,
					});
					return;
				}

				if (process.env.NODE_ENV !== "production") {
					console.warn("pageLinkFromMarkdown: missing parent to attach node");
				}
			},
		},
	};
}

export function replaceEmbeddedPageLinksInMarkdownText(
	value: string,
	rawValue = value
): Array<{ type: "text" | "html"; value: string }> {
	return replaceEmbeddedPageLinksInText(value, rawValue);
}
